import React, { createContext, useState, useEffect, useContext, useCallback, ReactNode } from 'react';
import { toast } from 'react-toastify';

interface DivisionItem {
    // Define the structure of your division data here
    id: string;
    name: string;
}

interface DivisionContextType {
    divisionData: any;
    setDivisionData: React.Dispatch<React.SetStateAction<any>>;
    fetchDivisionData: (page?: number) => Promise<void>;
    currentPage: number;
    totalPages: number;
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
    loading: boolean;
}

interface DivisionProviderProps {
    children: ReactNode;
}

const DivisionContext = createContext<DivisionContextType | undefined>(undefined);

const MAX_RETRIES = 5;
const RETRY_DELAY = 1000;

export const DivisionProvider: React.FC<DivisionProviderProps> = ({ children }) => {
    const [divisionData, setDivisionData] = useState<DivisionItem[]>(() => {
        const savedData = sessionStorage.getItem('divisionData');
        return savedData ? JSON.parse(savedData) : [];
    });
    const [loading, setLoading] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(() => {
        const savedPage = sessionStorage.getItem('currentPage');
        return savedPage ? parseInt(savedPage, 10) : 1;
    });
    const [totalPages, setTotalPages] = useState<number>(() => {
        const savedTotalPages = sessionStorage.getItem('totalPages');
        return savedTotalPages ? parseInt(savedTotalPages, 10) : 1;
    });

    const saveToSessionStorage = (key: string, value: any) => {
        sessionStorage.setItem(key, JSON.stringify(value));
    };

    const fetchDivisionData = useCallback(
        async (retries = 0): Promise<void> => {
        const authToken = sessionStorage.getItem('token');
        if (!authToken) {
            toast.error('Please Login Again', {
                position: 'bottom-right'
            });
            console.error('No auth token found');
            return;
        }

        try {
            setLoading(true);

            const response = await fetch(`https://backend-kyw8.onrender.com/divisions?page=${currentPage}`, {
                headers: {
                    "Authorization": `Bearer ${authToken}`
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('Unauthorized. Please login again.');
                }
                throw new Error('Something went wrong');
            }

            const data = await response.json();
            console.log(data)
            const divisions = data.divisions;
            const totalPages = data.totalPages;

            setDivisionData(divisions);
            console.log(divisionData)
            setCurrentPage(data.currentPage);
            setTotalPages(totalPages);
            saveToSessionStorage('divisionData', divisions);
            saveToSessionStorage('currentPage', data.currentPage);
            saveToSessionStorage('totalPages', totalPages);
        } catch (error: unknown) {
            if (error instanceof Error) {
                console.error('fetchDivisionData Error:', error.message);
                if (retries < MAX_RETRIES) {
                    const delay = RETRY_DELAY * (2 ** retries);
                    setTimeout(() => fetchDivisionData(retries + 1), delay);
                }
            } else {
                console.error('fetchDivisionData Error:', error);
            }
        } finally {
            setLoading(false);
        }
    },
     [currentPage, divisionData]
    )

    useEffect(() => {
        const authToken = sessionStorage.getItem('token');
        if (authToken) {
            fetchDivisionData();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <DivisionContext.Provider value={{ divisionData, setDivisionData, fetchDivisionData, currentPage, totalPages, setCurrentPage, loading }}>
            {children}
        </DivisionContext.Provider>
    );
};

export const useDivisionContext = () => {
    const context = useContext(DivisionContext);
    if (!context) {
        throw new Error('useDivisionContext must be used within a DivisionProvider');
    }
    return context;
};
